/**
 * Allys Contact Page Skeleton
 *
 * Matches ContactContentAllys: map hero, contact section
 * (form + info), agents, and CTA.
 * Includes navigation skeleton so the nav bar is always visible.
 */

import { AllysNavSkeleton } from './AllysNavSkeleton';

export function AllysContactSkeleton() {
  return (
    <main className="min-h-screen">
      <AllysNavSkeleton />

      {/* Map hero */}
      <section className="relative h-[40vh] min-h-[320px] bg-gray-200 skeleton-stagger-1">
        <div className="absolute inset-0 bg-linear-to-b from-[#243462]/30 to-[#243462]/60 skeleton-shimmer" />
        <div className="absolute bottom-8 left-0 right-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="h-10 w-64 rounded-lg bg-white/20 mb-2 skeleton-shimmer" />
            <div className="h-5 w-96 max-w-full rounded bg-white/10 skeleton-shimmer" />
          </div>
        </div>
      </section>

      {/* Contact section — form + info side by side */}
      <section className="py-20 lg:py-28 bg-white skeleton-stagger-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div className="space-y-6">
              <div className="h-8 w-48 rounded-lg bg-gray-200 skeleton-shimmer" />
              <div className="h-4 w-72 rounded bg-gray-100 skeleton-shimmer" />
              <div className="space-y-4 pt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="h-12 rounded-lg bg-gray-100 skeleton-shimmer" />
                  <div className="h-12 rounded-lg bg-gray-100 skeleton-shimmer" />
                </div>
                <div className="h-12 rounded-lg bg-gray-100 skeleton-shimmer" />
                <div className="h-12 rounded-lg bg-gray-100 skeleton-shimmer" />
                <div className="h-32 rounded-lg bg-gray-100 skeleton-shimmer" />
                <div className="h-12 w-40 rounded-xl bg-[#243462]/20 skeleton-shimmer" />
              </div>
            </div>
            <div className="space-y-8">
              <div className="h-8 w-40 rounded-lg bg-gray-200 skeleton-shimmer" />
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gray-100 shrink-0 skeleton-shimmer" />
                  <div className="space-y-2 flex-1">
                    <div className="h-5 w-24 rounded bg-gray-200 skeleton-shimmer" />
                    <div className="h-4 w-48 rounded bg-gray-100 skeleton-shimmer" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Agents */}
      <section className="py-20 lg:py-28 bg-gray-50 skeleton-stagger-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 space-y-3">
            <div className="h-4 w-20 rounded-full bg-gray-200 mx-auto skeleton-shimmer" />
            <div className="h-8 w-48 rounded-lg bg-gray-200 mx-auto skeleton-shimmer" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[1, 2].map((i) => (
              <div key={i} className="flex items-center gap-6 p-6 rounded-2xl bg-white border border-gray-100">
                <div className="w-20 h-20 rounded-full bg-gray-200 shrink-0 skeleton-shimmer" />
                <div className="space-y-2 flex-1">
                  <div className="h-5 w-32 rounded bg-gray-200 skeleton-shimmer" />
                  <div className="h-4 w-24 rounded bg-gray-100 skeleton-shimmer" />
                  <div className="h-3 w-40 rounded bg-gray-100 skeleton-shimmer" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#040813] skeleton-stagger-4">
        <div className="max-w-3xl mx-auto px-4 text-center space-y-6">
          <div className="h-10 w-72 rounded-lg bg-white/10 mx-auto skeleton-shimmer" />
          <div className="h-5 w-64 rounded bg-white/5 mx-auto skeleton-shimmer" />
          <div className="h-12 w-40 rounded-xl bg-[#BBA339]/20 mx-auto skeleton-shimmer" />
        </div>
      </section>
    </main>
  );
}
